import _extends from "@babel/runtime/helpers/extends";
import { ThemeProvider } from "@mui/material/styles";
import createGenerateClassName from "@mui/styles/createGenerateClassName";
import jssPreset from "@mui/styles/jssPreset";
import StylesProvider from "@mui/styles/StylesProvider";
import withStyles from "@mui/styles/withStyles";
import { createTheme, themes } from "../Utilities/ThemeHelper";
import classNames from "classnames";
import { create } from "jss";
import increaseSpecificity from "jss-increase-specificity";
import { SnackbarProvider } from "notistack";
import React, { useEffect, useMemo, useRef } from "react";
import { isWindowAvailable } from "../Services/windowService";
import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
import { styles as snackBarStyles } from "../SnackBarContent/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import CssBaseline from "@mui/material/CssBaseline";
import { TssCacheProvider } from "tss-react";
const env = process.env.NODE_ENV;
const StyledSnackbarProvider = withStyles(snackBarStyles)(SnackbarProvider);
const muiGenerateClassName = createGenerateClassName({
  productionPrefix: ""
});
const addSpecificity = ":not(#\\20)";
const cssResetProps = `{
	height: auto;
	background: none;
	background-image: none;
	box-shadow: none;
	margin: 0;
	padding: 0;
	border: 0;
	font-family: inherit;
	font-size: inherit;
	color:inherit;
}`;
export const stylisSpecificityPlugin = (repeatTimes = 0) => element => {
  if (repeatTimes === 0 || element.type !== "rule" || element.value.includes("*") || element.value === "html") {
    return;
  }
  const className = typeof element.props === "string" ? element.props : element.props.join("");
  const updatedValue = `${addSpecificity.repeat(repeatTimes)}${className}`;
  Object.assign(element, {
    props: [updatedValue]
  });
};
export const ProductThemeProvider = props => {
  const {
    children,
    cssReset = false,
    maxSnackbars = 3,
    SnackbarProps,
    SnackbarProviderProps = {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center"
      },
      hideIconVariant: true
    },
    prefix = "cpp",
    specificity = 0,
    product,
    primaryHex = "",
    title,
    themeOverride,
    maxBreakpoint,
    cache,
    useEmotionCache = true,
    cssBaseline = true
  } = props;
  const renderedCache = useMemo(() => {
    if (useEmotionCache) {
      if (cache) {
        if (cache.length !== 2) {
          throw new Error("Emotion cache must be an array of length 2");
        }
        return cache;
      }
      const localCache = createCache({
        key: prefix,
        prepend: true,
        speedy: env === "production",
        stylisPlugins: [stylisSpecificityPlugin(specificity)]
      });
      const tssCache = createCache({
        key: "tss",
        speedy: env === "production",
        stylisPlugins: [stylisSpecificityPlugin(specificity)]
      });
      return [localCache, tssCache];
    }
    return undefined;
  }, [specificity, prefix, useEmotionCache, cache]);
  useEffect(() => {
    if (maxBreakpoint && !["xs", "sm", "md", "lg", "xl"].includes(maxBreakpoint)) {
      // eslint-disable-next-line no-console
      console.warn("Unrecognized maxBreakpoint:", maxBreakpoint);
    }
  }, [maxBreakpoint]);
  useEffect(() => {
    if (product && product in themes === false) {
      // eslint-disable-next-line no-console
      console.warn("Unrecognized product:", product);
    }
  }, [product]);
  const theme = useMemo(() => {
    return createTheme(product, primaryHex, title, cssReset, themeOverride, maxBreakpoint);
  }, [cssReset, maxBreakpoint, primaryHex, product, themeOverride, title]);
  const jssRef = useRef(create({
    plugins: [...jssPreset().plugins, increaseSpecificity({
      repeat: specificity
    })]
  }));
  const makeSelector = (selector, selectorSpecificity) => {
    const specificityToAdd = specificity - selectorSpecificity < 0 ? 0 : specificity - selectorSpecificity;
    return specificity ? `${selector}${addSpecificity.repeat(specificityToAdd)}` : selector;
  };
  if (cssReset && isWindowAvailable()) {
    let resetElement = document.getElementById("preamble-reset");

    // This selector will catch all jss-styled items
    const preambleSelector = makeSelector(`[class^="${prefix}-"]`, 1);

    // jQuery UI adds a bunch of classnames, this catches those
    const jqueryUiSelector = `${makeSelector(`[class^="${prefix}-"]`, 2)} [class^="ui-"]`;

    // Sometimes we have styles on nested JSS selectors
    const preambleNestedSelector = `${makeSelector(`[class^="${prefix}-"]`, 2)} > *`;
    if (!resetElement) {
      const head = document.getElementsByTagName("head")[0];
      resetElement = document.createElement("style");
      resetElement.id = "preamble-reset";
      head.insertBefore(resetElement, head.firstChild);
    }
    resetElement.innerHTML = `${preambleSelector},${preambleNestedSelector},${jqueryUiSelector}${cssResetProps}`;
  }
  const generateClassNameDefault = (rule, sheet) => `${prefix}-${muiGenerateClassName(rule, sheet)}`;
  const StyledCssBaseline = cssReset ? ScopedCssBaseline : CssBaseline;
  const inner = /*#__PURE__*/React.createElement(StylesProvider, {
    jss: jssRef.current,
    generateClassName: generateClassNameDefault
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(StyledSnackbarProvider, _extends({
    maxSnack: maxSnackbars,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center"
    },
    hideIconVariant: true
  }, SnackbarProps, SnackbarProviderProps, {
    classes: {
      ...SnackbarProps?.classes,
      ...SnackbarProviderProps?.classes,
      containerRoot: classNames("prmbl-snackbarProvider", SnackbarProps?.classes?.containerRoot, SnackbarProviderProps?.classes?.containerRoot),
      root: classNames("prmbl-snackbarProvider-root", SnackbarProps?.classes?.root, SnackbarProviderProps?.classes?.root)
    }
  }), cssBaseline ? /*#__PURE__*/React.createElement(StyledCssBaseline, null, children) : children)));
  return useEmotionCache && renderedCache?.length === 2 ? /*#__PURE__*/React.createElement(CacheProvider, {
    value: renderedCache[0]
  }, /*#__PURE__*/React.createElement(TssCacheProvider, {
    value: renderedCache[1]
  }, inner)) : inner;
};
ProductThemeProvider.defaultProps = {
  primaryHex: "",
  specificity: 0,
  cssReset: false,
  maxSnackbars: 3,
  prefix: "cpp",
  SnackbarProps: {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center"
    },
    hideIconVariant: true
  }
};
export default ProductThemeProvider;